<template>
  <v-row no-gutters>
    <feature-card-basic ref="featureCard" @update:feature="hydrantFetched" :layer="layer">
      <template #top v-if="isHydrantInspectionsVisible">
        <v-row no-gutters class="pr-5 pl-6 pb-2">
          <v-col cols="auto">
            <v-subheader class="text-subtitle-2 font--text" v-text="$i18n.t('featureCard.inspections')"> </v-subheader>
          </v-col>
          <v-spacer> </v-spacer>
          <v-col cols="auto">
            <base-rounded-button
              icon="mdi-plus"
              size="x-small"
              color="success"
              @click="toggleHydrantInspectionAdding"
              :tooltip="$i18n.t('sidebar.hydrants_inspectionsModule.addOne')"
              v-if="permissionsHydrantsInspections.main_value > 1"
            />
          </v-col>
        </v-row>
        <sidebar-row
          class="pr-5"
          icon="mdi-calendar-alert"
          icon-color="rgba(0,0,0,0.7)"
          align-class="align-start"
          border-bottom
          v-if="nextInspectionDate"
        >
          <v-row no-gutters>
            <v-col>
              <v-row no-gutters>
                <v-col cols="auto">
                  <v-subheader
                    :class="`${daysClass} text-subtitle-2 pr-1 px-0`"
                    style="height: auto;"
                    v-text="nextInspectionDate"
                  >
                  </v-subheader>
                </v-col>
                <v-col cols="auto">
                  <v-subheader
                    class="text-caption pa-0 font--text"
                    style="height: auto;"
                    v-text="nextInspectionDaysText"
                  >
                  </v-subheader>
                </v-col>
              </v-row>
              <v-row no-gutters class="pb-4">
                <v-col cols="auto">
                  <v-subheader
                    class="success-text text-caption pr-2 font-italic px-0"
                    style="height: auto;"
                    v-text="lastInspectionText"
                  >
                  </v-subheader>
                </v-col>
                <v-col cols="auto" v-if="lastInspectionDate">
                  <link-underlined
                    @click="toggleInspections"
                    :translation-path="inspectionsVisible ? 'featureCard.hide' : 'featureCard.seeAll'"
                  />
                </v-col>
              </v-row>
              <v-row no-gutters v-if="inspectionsVisible">
                <inspections-list
                  :items="currentHydrantInspections"
                  @openInspection="openInspection"
                  class="pr-1 pb-7"
                />
              </v-row>
            </v-col>
          </v-row>
        </sidebar-row>
        <v-row no-gutters v-else>
          <v-col cols="auto" class="pl-10">
            <v-subheader
              class="success-text text-caption pr-2 font-italic px-0"
              style="height: auto;"
              v-text="$i18n.t('sidebar.noInspections')"
            >
            </v-subheader>
          </v-col>
        </v-row>
      </template>
    </feature-card-basic>
    <hydrant-inspection
      v-if="isHydrantInspectionLoaded"
      :current-inspection-id="currentInspectionId"
      :is-visible.sync="isHydrantInspectionVisible"
      @refreshInspectionsList="init(true)"
    />
  </v-row>
</template>

<script>
import { call, get } from 'vuex-pathify';

import FeatureCardBasic from '@/components/FeatureCardBasic';
import InspectionsList from '@/components/InspectionsList';
import LinkUnderlined from '@/components/LinkUnderlined';
import SidebarRow from '@/components/SidebarRow';

export default {
  name: 'FeatureCardHydrant',
  components: {
    SidebarRow,
    LinkUnderlined,
    InspectionsList,
    FeatureCardBasic,
    HydrantInspection: () => import('@/components/HydrantInspection'),
  },
  props: {
    layer: {
      type: Object,
      default: () => {},
    },
    localeDateFormat: {
      validatior: value => {
        return ['ll', 'l', 'LL', 'L'].indexOf(value) !== -1;
      },
      default: 'L',
    },
  },
  data: () => ({
    currentInspectionId: null,
    isHydrantInspectionVisible: false,
    isHydrantInspectionLoaded: false,
    statusesClasses: {
      ok: 'success--text',
      soon_past_due: 'warning--text',
      past_due: 'error--text',
    },
    currentHydrantFeature: undefined,
    inspectionsVisible: false,
  }),
  computed: {
    isAdditionalModuleEnabled: get('admin/additionalModules@HYDRANTS_INSPECTIONS.enabled'),
    currentHydrantInspections: get('hydrants/hydrantsInspections@:currentHydrantId'),
    hydrantsLayerId: get('admin/modulesMapping@hydrants_inspections.hydrants_layer_id'),
    hydrantsInspectionsDataSourceName: get('admin/modulesMapping@hydrants_inspections.inspections_datasource_name'),
    hydrantsInspectionsDateStatuses: get('admin/modulesMapping@hydrants_inspections.next_inspection_date_statuses'),
    isModuleEnabled: get('admin/settingsValues@hydrants_inspections_module_enabled'),
    permissions: get('users/toolsPermissions'),
    currentHydrantId() {
      return this.$route.params.fid;
    },
    daysClass() {
      const nextInspectionDateStatusKey = Object.keys(this.hydrantsInspectionsDateStatuses).find(
        key => this.hydrantsInspectionsDateStatuses[key] === this.nextInspectionDateStatus
      );
      return this.statusesClasses[nextInspectionDateStatusKey];
    },
    isHydrantInspectionsVisible() {
      return (
        this.currentHydrantId !== 'new' &&
        this.isModuleEnabled &&
        this.isAdditionalModuleEnabled &&
        this.permissionsHydrantsInspections.main_value > 0
      );
    },
    nextInspectionDateStatus() {
      return this.currentHydrantFeature.properties._next_inspection_date_status;
    },
    nextInspectionDate() {
      if (!this.currentHydrantFeature.properties._next_inspection_date) {
        return null;
      }
      return this.$dayjs(this.currentHydrantFeature.properties._next_inspection_date).format(this.localeDateFormat);
    },
    nextInspectionDays() {
      // hours, minutes and seconds are ignored
      const nextInspectionDate = this.$dayjs(
        this.currentHydrantFeature?.properties?._next_inspection_date.split('T')[0]
      );
      const nowDate = this.$dayjs()
        .hour(0)
        .minute(0)
        .second(0)
        .millisecond(0);
      return nextInspectionDate.diff(nowDate, 'day');
    },
    nextInspectionDaysText() {
      let days = this.nextInspectionDays;
      if (days < 0) {
        days = days * -1;
        return `(${days} ${this.$i18n.tc('sidebar.days', days)} ${this.$i18n.t('sidebar.afterDeadline')})`;
      }
      return `(${this.$i18n.t('sidebar.for')} ${days} ${this.$i18n.tc('sidebar.days', days)})`;
    },
    lastInspectionDate() {
      if (!this.currentHydrantFeature.properties._last_inspection_date) {
        return null;
      }
      return this.$dayjs(this.currentHydrantFeature.properties._last_inspection_date).format(this.localeDateFormat);
    },
    lastInspectionText() {
      if (!this.lastInspectionDate) {
        return this.$i18n.t('sidebar.noLastInspction');
      }
      const lastInspectionDate = this.lastInspectionDate;
      return `${this.$i18n.t('featureCard.lastInspection')}: ${lastInspectionDate}`;
    },
    permissionsHydrantsInspections() {
      return this.permissions['HYDRANTS_INSPECTIONS'];
    },
  },
  watch: {
    currentHydrantId(nV) {
      if (nV || nV == '0') {
        this.init();
      }
    },
    isHydrantInspectionVisible(nV) {
      if (!nV) this.currentInspectionId = null;
    },
  },
  methods: {
    getHydrantsInspections: call('hydrants/getHydrantsInspections'),
    hydrantFetched(feature) {
      this.currentHydrantFeature = feature;
    },
    openInspection(id) {
      this.currentInspectionId = id;
      this.isHydrantInspectionVisible = true;
      this.isHydrantInspectionLoaded = true;
    },
    prepareHydrantInspectionsFilter() {
      return {
        $EQUAL: { [`${this.hydrantsInspectionsDataSourceName}.hydrant_id`]: this.currentHydrantId },
      };
    },
    toggleInspections() {
      this.inspectionsVisible = !this.inspectionsVisible;
    },
    toggleHydrantInspectionAdding() {
      this.openInspection('new');
    },
    async init(refreshFeatureCard = false) {
      if (!this.isHydrantInspectionsVisible) {
        return;
      }
      this.isHydrantInspectionVisible = false;
      const payload = {
        features_filter: this.prepareHydrantInspectionsFilter(),
        sorting: [
          {
            attribute_name: 'date_carried_out',
            direction: 'desc',
          },
        ],
        hydrantId: this.currentHydrantId,
      };
      await this.getHydrantsInspections(payload);
      if (refreshFeatureCard) {
        this.$refs.featureCard.onLidFidChanged(
          { lid: this.hydrantsLayerId, fid: this.currentHydrantId },
          { lid: this.hydrantsLayerId, fid: this.currentHydrantId }
        );
      }
    },
  },

  mounted() {
    this.init();
  },
};
</script>

<style lang="scss" scoped>
.box--border {
  @include border(bottom);
}
</style>
